"use client";

import { Instagram, Linkedin, Loader, Twitter, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function CommissionForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-border bg-white">
        <p className="font-default text-xl text-foreground italic">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-secondary bg-transparent text-secondary hover:bg-secondary hover:text-white">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <FormField control={control} name="full_name" rules={{
        required: "Full name is required",
        minLength: {
          value: 2,
          message: "Min 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-default">
                Full Name *
              </FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" {...field} className="border-0 border-b-2 border-border bg-transparent px-0 h-12 text-xl font-default text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-primary" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email_address" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-default">
                Email Address *
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" {...field} className="border-0 border-b-2 border-border bg-transparent px-0 h-12 text-xl font-default text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-primary" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone_number" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-default">
                Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 123-4567" {...field} className="border-0 border-b-2 border-border bg-transparent px-0 h-12 text-xl font-default text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-primary" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="project_type" rules={{
      required: "Please select a project type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-default">
              Inquiry Type *
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="border-0 border-b-2 border-border bg-transparent px-0 h-12 text-xl font-default text-foreground focus-visible:ring-0 focus-visible:border-primary w-full">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-card border-border">
                <SelectItem value="Executive Search">Executive Search</SelectItem>
                <SelectItem value="Technical Recruitment">Technical Recruitment</SelectItem>
                <SelectItem value="Finance Placement">Finance Placement</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="budget_range" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-default">
              Experience Level
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="border-0 border-b-2 border-border bg-transparent px-0 h-12 text-xl font-default text-foreground focus-visible:ring-0 focus-visible:border-primary w-full">
                  <SelectValue placeholder="Select level" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-card border-border">
                <SelectItem value="Entry Level">Entry Level</SelectItem>
                <SelectItem value="Mid-Senior">Mid-Senior</SelectItem>
                <SelectItem value="Executive">Executive</SelectItem>
                <SelectItem value="C-Suite">C-Suite</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="project_description" rules={{
      required: "Description is required",
      minLength: {
        value: 10,
        message: "Min 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-default">
              Message *
            </FormLabel>
            <FormControl>
              <Textarea rows={6} placeholder="Tell us about your hiring needs or career goals." {...field} className="border-0 border-b-2 border-border bg-transparent px-0 text-xl font-default text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-primary resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="reference_images" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-default">
              Resume / Documents
            </FormLabel>
            <FormControl>
              <label className="flex items-center justify-center gap-3 border border-dashed border-border bg-transparent py-8 cursor-pointer hover:border-primary hover:bg-muted transition-colors">
                <Upload className="size-4 text-muted-foreground" />
                <span className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-default">
                  + Upload File
                </span>
                <input type="file" className="hidden" multiple onChange={e => field.onChange(e.target.files)} />
              </label>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive bg-destructive/10">
          <p className="text-destructive text-sm font-default">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 text-sm tracking-[0.25em] uppercase font-bold transition-shadow font-default">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Processing...
          </> : "Submit Request →"}
      </Button>

      <p className="font-default italic text-sm text-muted-foreground text-center">
        Our team will review your profile within 24 hours.
      </p>
    </Form>;
}
export default function TalentRequest() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const contactRows = [{
    label: "Headquarters",
    value: "Nexus Plaza, 100 Financial District, New York, NY"
  }, {
    label: "Direct Line",
    value: "+1 (212) 555-0199"
  }, {
    label: "Inquiries",
    value: "talent@nexuspartners.com"
  }, {
    label: "Availability",
    value: "Mon-Fri, 9am - 6pm EST"
  }];
  const socials = [{
    name: "Instagram",
    icon: Instagram
  }, {
    name: "Twitter",
    icon: Twitter
  }, {
    name: "LinkedIn",
    icon: Linkedin
  }];
  return <section data-section-id="2991" id="talent-request" className="bg-background text-foreground pt-32 md:pt-44 lg:pt-[180px] pb-24 px-6 md:px-12 lg:px-20 relative overflow-hidden">
      <div ref={ref} className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5 space-y-12">
            <div className={`flex items-center gap-3 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <span className="size-2 bg-primary" />
              <span className="text-[11px] tracking-[0.25em] uppercase font-default text-muted-foreground">
                / Talent Acquisition
              </span>
            </div>

            <h2 className={`font-default font-black leading-[0.9] transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <span className="block text-5xl md:text-6xl lg:text-[6rem] tracking-tighter">CONNECT /</span>
              <span className="block text-5xl md:text-6xl lg:text-[6rem] tracking-tighter ml-0 md:ml-12 lg:ml-[60px] text-primary">
                YOUR /
              </span>
              <span className="block text-5xl md:text-6xl lg:text-[6rem] tracking-tighter ml-0 md:ml-24 lg:ml-[120px]">
                FUTURE
              </span>
            </h2>

            <p className={`font-default text-lg leading-[1.7] text-muted-foreground max-w-md transition-all duration-1000 delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              Nexus Talent Partners bridges the gap between industry-leading organizations and elite professionals. Start your journey by submitting your requirements or credentials below.
            </p>

            <div className="border-t border-border">
              {contactRows.map((row, i) => <div data-index={i} key={row.label} className="flex flex-col md:flex-row md:items-baseline gap-2 md:gap-8 py-6 border-b border-border">
                  <span className="text-[11px] tracking-[0.25em] uppercase font-default text-muted-foreground md:w-44 shrink-0">
                    {row.label}
                  </span>
                  <span className="font-default text-xl text-foreground leading-snug">{row.value}</span>
                </div>)}
            </div>

            <div className="flex items-center gap-6">
              {socials.map((social, i) => {
              const Icon = social.icon;
              return <Button data-index={i} key={social.name} type="button" variant="outline" size="icon" aria-label={social.name} className="size-12 border-border text-foreground hover:text-primary hover:border-primary hover:bg-transparent">
                    <Icon className="size-5" strokeWidth={1} />
                  </Button>;
            })}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className={`bg-muted/30 backdrop-blur-xl border border-border p-8 md:p-12 transition-all duration-1000 delay-300 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <div className="flex items-center justify-between mb-10 pb-6 border-b border-border">
                <span className="text-[11px] tracking-[0.25em] uppercase font-default text-muted-foreground">
                  Inquiry Portal
                </span>
                <span className="text-[11px] tracking-[0.25em] uppercase font-default text-primary">
                  001 / 005
                </span>
              </div>

              <FormProvider formKey="talent_request_form_employers" formId="talent_request_form_employers" sectionName="talent-request" defaultValues={{
              full_name: "",
              email_address: "",
              phone_number: "",
              project_type: "",
              project_description: "",
              budget_range: "",
              reference_images: null
            }} validationRules={{
              full_name: {
                required: true,
                minLength: 2
              },
              email_address: {
                required: true,
                pattern: "email"
              },
              phone_number: {},
              project_type: {
                required: true
              },
              project_description: {
                required: true,
                minLength: 10
              },
              budget_range: {}
            }} submitText="Submit Request" successMessage="Thank you for contacting Nexus Talent Partners. Our recruitment team will review your inquiry and reach out within 24 hours." errorMessage="Submission failed. Please verify your details and try again.">
                <CommissionForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}